import Vue from 'vue'

import { cloneDeep, isEqual } from 'lodash'

import { Services } from '@sigma-frontend-libs/commons'

import { generateServices, globalErrorHandler, permissionPresets, states } from '@/utils'

import { errorProcessor, errorProcessorHandler } from '@/components/services/contacts/utils'

import render from './render'

export default {
  name: 'Dialogs',

  mixins: [
    generateServices([
      {
        name: Services.contacts,

        inputFilter(result) {
          result.custom19 = result.custom19 || undefined

          return result
        },

        outputFilter(result) {
          if (!result.ListId) {
            result.ListId = this.ListId
          }
          for (const key in result) {
            if (result[key] === undefined) {
              result[key] = null
            }
          }

          return result
        },

        find: false,
        remove: {
          redirect: false
        },

        errorProcessor: errorProcessorHandler
      },
      {
        name: Services.contactLists,

        find: false,
        remove: {
          redirect: Services.contacts
        }
      }
    ])
  ],

  props: {
    id: String,
    type: String,

    show: {
      type: Boolean,
      default: false
    },

    ListId: String
  },

  data() {
    return {
      initialData: undefined
    }
  },

  computed: {
    canGetContacts() {
      return this.checkPermissions(`advanced.${Services.contacts}.get`, permissionPresets.meUp)
    },

    canCreateContacts() {
      return this.checkPermissions(`advanced.${Services.contacts}.create`, permissionPresets.meUp)
    },

    canUpdateContacts() {
      return this.checkPermissions(`advanced.${Services.contacts}.update`, permissionPresets.meUp)
    },

    canRemoveContacts() {
      return this.checkPermissions(`advanced.${Services.contacts}.remove`, permissionPresets.meUp)
    },

    actionByType() {
      if (this.type) {
        const result = {}
        const [ service, method ] = this.type.split('.')
        Object.assign(result, {
          service,
          method
        })

        return result
      }
    },

    dataIsEqual() {
      if (!this.actionByType) {
        return false
      }

      const { service, method } = this.actionByType
      if (method !== 'update') {
        return false
      }

      return isEqual(
        this.normalizeDataForCompare(this.initialData),
        this.normalizeDataForCompare(this.restData[service].get.data)
      )
    },

    actionDisabled() {
      if (!this.actionByType) {
        return false
      }

      const { service, method } = this.actionByType
      if (this.restData[service][method].state === states.loading) {
        return true
      }

      if (method === 'remove') {
        return false
      }

      if (!this.restData[service][method].isValid) {
        return true
      }

      if (method === 'update' && this.dataIsEqual) {
        return true
      }

      return false
    }
  },

  watch: {
    id() {
      this.getByService()
    }
  },

  mounted() {
    this.getByService()
  },

  methods: {
    normalizeDataForCompare(value) {
      if (value === null) {
        return undefined
      }

      if (Array.isArray(value)) {
        return value
          .map(item => {
            return this.normalizeDataForCompare(item)
          })
          .filter(item => {
            return item !== undefined
          })
      }

      if (value && typeof value === 'object') {
        const result = {}

        for (const key of Object.keys(value)) {
          const normalizedValue = this.normalizeDataForCompare(value[key])
          if (normalizedValue !== undefined) {
            result[key] = normalizedValue
          }
        }

        return result
      }

      return value
    },

    async get(service) {
      if (!this.canGetContacts || !this.id) {
        this.initialData = undefined

        return
      }

      try {
        const result = await this.rest[service].get(this.id)
        if (result) {
          this.initialData = cloneDeep(result)
        } else {
          this.initialData = undefined
        }
      } catch (error) {
        globalErrorHandler(error)
      }
    },

    async create(service) {
      if (!this.canCreateContacts || !this.restData[service].create.isValid) {
        return
      }

      try {
        const result = await this.rest[service].create(this.restData[service].create.data)
        if (result) {
          if (service === Services.contactLists) {
            Vue.router.push({
              name: `${Services.contacts}.single`,
              params: { id: result.id }
            })
          }

          this.$emit('show', false)
        }
      } catch (error) {
        if (service === Services.contacts) {
          errorProcessorHandler.call(this, errorProcessor.call(this, error))
        } else {
          globalErrorHandler(error)
        }
      }
    },

    async update(service) {
      if (!this.canUpdateContacts || this.actionDisabled) {
        return
      }

      try {
        const result = await this.rest[service].update(this.restData[service].get.data.id, this.restData[service].get.data)
        if (result) {
          this.initialData = cloneDeep(result)
          this.$emit('show', false)
        }
      } catch (error) {
        globalErrorHandler(error)
      }
    },

    async remove(service) {
      if (!this.canRemoveContacts || !this.id) {
        return
      }

      try {
        const result = await this.rest[service].remove(this.id)
        if (result) {
          this.$emit('show', false)
        }
      } catch (error) {
        globalErrorHandler(error)
      }
    },

    getByService() {
      if (this.type) {
        const [ service, action ] = this.type.split('.')
        if (action === 'update') {
          this.get(service)
        } else {
          this.initialData = undefined
        }
      } else {
        this.initialData = undefined
      }
    }
  },

  render
}
